import { Box } from "@mui/material";
import { FirstPage, ChevronLeft, ChevronRight, LastPage } from "@mui/icons-material";
import { FC, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import type { SortingState } from "@tanstack/react-table";

import "./kigyoInfoDtail.css";
import PrintDialog from '../printDialog/printDialog';
import "../css/style.css";
import { GreenButton, GreenChoiceButton } from './kigyoInfo.style';
import type { KigyoDetail } from "./kigyo.type";
import { KIGYO_DETAIL_ITEMS } from "./kigyoSelectItems";

import { sessionDisconnectedCheck } from '../../shared/api/sessionDisconnectedCheck';
import { fetchAuthSession } from 'aws-amplify/auth';
import { OrderBy, SearchCrudApiRequest } from '../../shared/model/apiRequestModel';
import crud from '../../shared/api/crud';
import { SystemErrorMsg, SystemErrorTitle } from '../../constants';
import { BackdropLoading } from "../../shared/spinner/backdropLoading";

//PDF出力ダイアログ表示用の型
type Kind = "詳細";
type PrintInfo = {
    kind: Kind;
    start: number;
    end: number;
    total: number;
    sorting: SortingState;
    condition: Conditions[] | undefined;
}

const screenId = "U051"

type DetailSort = {
    recordId?: number;
    sorting?: SortingState;
    pageIndex?: number;
    searchConditions?: Conditions[];
    searchItemsState?: { [key: string]: SearchItemState }
    resultCount?: number;
}

type Conditions = {
    operator: string;
    itemLabel: string;
    count: number;
    value: string;
}

interface SearchItemState {
    itemCode: string;
    operator: 'AND' | 'OR';
    inputValue: string;
    inputLabel?: string;
    matchFlag?: boolean;
}

// ソートカラムのマップ定義
const sortColumn: { [key: string]: string } = {
    "SHOGO_KANJI": "SHOGO_KANA",
    "JUSHO": "JUSHO_CODE",
    "DAIHYOSHA_KANJI": "DAIHYOSHA_KANA",
    "KIGYO_CODE": "KIGYO_CODE",
    "YUBIN_NO": "YUBIN_NO",
    "SHIHONKIN": "SHIHONKIN",
    "JUGYOINSU": "JUGYOINSU",
    "URIAGE1": "URIAGE1",
    "RIEKI1": "RIEKI1"
}

//複数の値がある場合に並べる関数
const kigyoInfoJoin = (
    k: KigyoDetail | undefined,
    itemName: string,
    max: number,
): string => {
    if (!k) return "";

    const values: string[] = [];

    for (let i = 1; i <= max; i++) {
        const key = `${itemName}${i}` as keyof KigyoDetail;
        const v = k[key];

        //途中で値が途切れたら終了
        if (v == null) continue;

        //空文字はpushしない
        const s = String(v).trim();
        if (s === "") continue;

        values.push(String(v));
    };

    return values.join(", ");
};

//未定義やnullの場合は空文字を、それ以外は文字列とする
const valueToText = (x: unknown) => (x === null || x === undefined ? "" : String(x));

const KigyoInfoDetail: FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    //一覧のソート順で表示する
    const { state } = useLocation() as { state?: DetailSort };

    const [data, setData] = useState<KigyoDetail[] | null>(null);

    const sortInfoId = state?.sorting?.[0]?.id ?? "";
    const sortInfoDescBoolean = state?.sorting?.[0]?.desc ?? true;

    useEffect(() => {
        const interval = setInterval(async () => {
            // セッションの有効性確認
            if (await sessionDisconnectedCheck(screenId, "polling", navigate)) {
                return;
            }
        }, 30000);
        return () => clearInterval(interval);
    }, []);

    const [index, setIndex] = useState<number>(state?.recordId !== undefined ? state?.recordId - 1 : -1);

    useEffect(() => {

        const sortInfoDesc: OrderBy["sortRule"] = sortInfoDescBoolean ? "DESC" : "ASC";

        const fetchData = async () => {

            // セッションの有効性確認
            if (await sessionDisconnectedCheck(screenId, 'getKigyoInfoDetailData', navigate)) {
                return
            }
            setLoading(true)
            const session = await fetchAuthSession();
            const email = session.tokens?.idToken?.payload?.email;
            const sessionId = session.tokens?.idToken?.payload?.sessionId;
            const accountInfoData = JSON.parse(session.tokens?.idToken?.payload?.accountInfoData?.toString() || "[]");
            const contract = JSON.parse(session?.tokens?.idToken?.payload.contractInfoData?.toString() || "[]");
            const contractForm = contract.length !== 0 && contract[0].contractForm;
            const offset = index;

            const request: SearchCrudApiRequest = {
                sessionId: sessionId ? sessionId.toString() : '',
                userMailAddress: email ? email.toString() : '',
                processFlag: "0",
                countFlag: "0",
                contractForm: contractForm,
                targetTableName: "T_SEARCH_RESULT_TMP",
                selectItems: KIGYO_DETAIL_ITEMS,
                conditions: [
                    {
                        targetColumn: "MAIL_ADDRESS",
                        targetValue: [email],
                    },
                ],
                conditionsOperator: "and",
                orderBy: [
                    {
                        sortColumn: sortColumn[sortInfoId],
                        sortRule: sortInfoDesc,
                    },
                    {
                        sortColumn: "KIGYO_CODE",
                        sortRule: "ASC",
                    },
                ],
                usingStartDate: contract.length !== 0 && accountInfoData[0].usingStartDate,
                limit: 1,
                offset: offset
            };

            const response = crud(request);

            if ((await response).resCd === "E9999") {
                navigate("/Error", { state: { title: SystemErrorTitle, message: SystemErrorMsg } });
            } else {
                setData((await response).data?.resultData);
            }
            setLoading(false)
        }
        fetchData();

    }, [index]);

    const dispSearch = async () => {

        // セッションの有効性確認
        if (await sessionDisconnectedCheck(screenId, 'handleClickDispInputSearchCondition', navigate)) {
            return
        }

        navigate("/search", {
            state: {
                fromResult: state?.searchConditions,
                searchItemsState: state?.searchItemsState,
            }
        });
    }
    const dispKigyoInfoList = async () => {

        // セッションの有効性確認
        if (await sessionDisconnectedCheck(screenId, 'handleClickDispKigyoInfoList', navigate)) {
            return
        }

        const searchConditions = state?.searchConditions;
        const searchItemsState = state?.searchItemsState;
        const sorting = state?.sorting;
        const pageIndex = state?.pageIndex;
        const resultCount = state?.resultCount;

        navigate("/result", { state: { sorting, pageIndex, searchConditions, searchItemsState, resultCount } });
    }

    const dataList: KigyoDetail[] = Array.isArray(data) ? (data as unknown as KigyoDetail[]) : [];
    const kigyo = dataList[0];
    const total = state?.resultCount === undefined ? 1 : state.resultCount;

    const indexRef = useRef(false);

    useEffect(() => {
        if (!dataList.length || indexRef.current) return;

        const raw = Number((state as any)?.recordId);
        const n = Math.max(0, raw - 1);
        setIndex(n);

        indexRef.current = true;
    }, [dataList, state?.recordId]);

    useEffect(() => {
        const maxIdx = Math.max(total - 1, 0);
        setIndex((i) => Math.max(0, Math.min(i, maxIdx)));
    }, [dataList.length]);

    //業績データを整列する
    const tableEmptyCheck = (arr: (string | number | null | undefined)[], keep: boolean[]) => {
        const filled = arr.filter((_, i) => keep[i]).map(v => (v == null || v === "" ? "" : v));
        const empties = Array(arr.length - filled.length).fill("");
        return [...filled, ...empties];
    };

    //指定した番号に飛ぶ
    const [pageText, setPageText] = useState("");
    const [errorText, setErrorText] = useState("");

    const handleJumpPage = async () => {
        // セッションの有効性確認
        if (await sessionDisconnectedCheck(screenId, 'handleClickJumpPage', navigate)) {
            return
        }

        const num = Number(pageText);

        //未入力エラーと指定範囲外エラー
        if (pageText == "" || !Number.isFinite(num)) {
            setErrorText("番号を入力してください");
            return;
        } else if (num < 1 || num > total) {
            setErrorText("指定した番号はページの範囲外です");
            return;
        }

        const newIndex = num - 1;
        setIndex(newIndex);
        setPageText("");
        setErrorText("");
    };

    const [printDialogOpen, setPrintDialogOpen] = useState(false);
    const [printInfo, setPrintInfo] = useState<PrintInfo>({
        kind: "詳細",
        start: 0,
        end: 0,
        total: 0,
        sorting: [{ id: "URIAGE", desc: true }],
        condition: state?.searchConditions,
    });

    const handleOpen = async () => {
        // セッションの有効性確認
        if (await sessionDisconnectedCheck(screenId, 'handleOpen', navigate)) {
            return
        }

        setPrintInfo({
            kind: "詳細",
            start: index + 1,
            end: index + 1,
            total: total,
            sorting: state?.sorting ?? [{ id: "URIAGE", desc: true }],
            condition: state?.searchConditions,
        })

        setPrintDialogOpen(true);
    };

    if (data === null) return null;

    return (
        <>
            <BackdropLoading open={loading} />
            <Box className="headerLabel">
                企業情報詳細
            </Box>
            <dl className="detail" style={{ userSelect: "none" }}>
                <div className="d1">
                    <dt className="dt_template">企業コード</dt>
                    <dd className="dd_code">{valueToText(kigyo.KIGYO_CODE)}</dd>
                    <dt className="dt_template">上場</dt>
                    <dd className="dd_jojo">{valueToText(kigyo.JOJO)}</dd>
                    <dt className="dt_template">調査年月日</dt>
                    <dd className="dd_code">{valueToText(kigyo.CHOSA_NENGAPPI)}</dd>
                </div>
                <div className="d1">
                    <dt className="dt_template">商号（カナ）</dt>
                    <dd className="dd_shougou">{valueToText(kigyo.SHOGO_KANA)}</dd>
                    <dt className="dt_template">代表者カナ</dt>
                    <dd className="dd_shougou">{valueToText(kigyo.DAIHYOSHA_KANA)}</dd>
                </div>
                <div className="d1">
                    <dt className="dt_template">商号（漢字）</dt>
                    <dd className="dd_shougou">{valueToText(kigyo.SHOGO_KANJI)}</dd>
                    <dt className="dt_template">代表者氏名</dt>
                    <dd className="dd_shougou">{valueToText(kigyo.DAIHYOSHA_KANJI)}</dd>
                </div>
                <div className="d1">
                    <dt className="dt_template">郵便番号</dt>
                    <dd className="dd_yubin">
                        {valueToText(kigyo.YUBIN_NO) === "" ? "" : `〒${valueToText(kigyo.YUBIN_NO)}`}
                    </dd>
                    <dt className="dt_template">所在地</dt>
                    <dd className="dd_jusho">{valueToText(kigyo.JUSHO)}</dd>
                </div>
                <div className="d1">
                    <dt className="dt_template">電話番号</dt>
                    <dd className="dd_denwa">{valueToText(kigyo.DENWA_NO)}</dd>
                    <dt className="dt_template">設立年月</dt>
                    <dd className="dd_denwa">{valueToText(kigyo.SETSURITSU_NENGETSU)}</dd>
                    <dt className="dt_template">創業年月</dt>
                    <dd className="dd_denwa">{valueToText(kigyo.SOGYO_NENGETSU)}</dd>
                </div>
                <div className="d1">
                    <dt className="dt_template">資本金</dt>
                    <dd className="dd_shihonkin">
                        {valueToText(kigyo.SHIHONKIN) === "" ? "" : <><span>{valueToText(kigyo.SHIHONKIN)}</span><span className="unit">千円</span></>}
                    </dd>
                    <dt className="dt_template">従業員数</dt>
                    <dd className="dd_shihonkin">
                        {valueToText(kigyo.JUGYOINSU) === "" ? "" : <><span>{valueToText(kigyo.JUGYOINSU)}</span><span className="unit">人</span></>}
                    </dd>
                </div>
                <div className="d1">
                    <dt className="dt_template">業種</dt>
                    <dd className="dd_gyoshu">{valueToText(kigyo.GYOSHU1)}</dd>
                    <dd className="dd_gyoshu">{valueToText(kigyo.GYOSHU2)}</dd>
                    <dd className="dd_gyoshu">{valueToText(kigyo.GYOSHU3)}</dd>
                </div>
                <div className="d1">
                    <dt className="dt_template">営業種目</dt>
                    <dd className="dd_shumoku">{kigyoInfoJoin(kigyo, "EIGYO_SHUMOKU", 10)}</dd>
                    <dt className="dt_template">営業所・支店</dt>
                    <dd className="dd_shumoku">{kigyoInfoJoin(kigyo, "EIGYOSHO_SHITEN", 10)}</dd>
                </div>
                <div className="d1">
                    <dt className="dt_template">役員</dt>
                    <dd className="dd_shumoku">{kigyoInfoJoin(kigyo, "YAKUIN", 10)}</dd>
                    <dt className="dt_template">仕入先</dt>
                    <dd className="dd_shumoku">{kigyoInfoJoin(kigyo, "SHIIRESAKI", 10)}</dd>
                </div>
                <div className="d1">
                    <dt className="dt_template">大株主</dt>
                    <dd className="dd_shumoku">{kigyoInfoJoin(kigyo, "KABUNUSHI", 10)}</dd>
                    <dt className="dt_template">販売先</dt>
                    <dd className="dd_shumoku">{kigyoInfoJoin(kigyo, "HAMBAISAKI", 10)}</dd>
                </div>
                <div className="field">
                    <div style={{ width: "50%" }}>
                        <div className="d2">
                            <dt className="dt_template">業績</dt>
                            <dd className="dd_gyoseki">
                                <table className="gyoseki_table">
                                    <colgroup>
                                        <col style={{ width: "20%" }} />
                                        <col style={{ width: "30%" }} />
                                        <col style={{ width: "30%" }} />
                                        <col style={{ width: "20%" }} />
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th>決算年月</th>
                                            <th>売上（千円）</th>
                                            <th>利益（千円）</th>
                                            <th>配当総額</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => {
                                            const srcK = [kigyo?.KESSAN_NENGETSU3, kigyo?.KESSAN_NENGETSU2, kigyo?.KESSAN_NENGETSU1];
                                            const keep = srcK.map(v => v != null && v !== "");
                                            const k = tableEmptyCheck(srcK, keep);
                                            const m = tableEmptyCheck([kigyo?.MIKOMI_SUITEI_KUBUN3, kigyo?.MIKOMI_SUITEI_KUBUN2, kigyo?.MIKOMI_SUITEI_KUBUN1], keep);
                                            const u = tableEmptyCheck([kigyo?.URIAGE3, kigyo?.URIAGE2, kigyo?.URIAGE1], keep);
                                            const r = tableEmptyCheck([kigyo?.RIEKI3, kigyo?.RIEKI2, kigyo?.RIEKI1], keep);
                                            const h = tableEmptyCheck([kigyo?.HAITO_SOGAKU3, kigyo?.HAITO_SOGAKU2, kigyo?.HAITO_SOGAKU1], keep);

                                            return Array.from({ length: 3 }).map((_, i) => (
                                                <tr className="gyoseki_tr" key={i}>
                                                    <td>{k[i] ? `${k[i]}` : ""}</td>
                                                    <td>{Number.isNaN(u[i]) || u[i] === "" ? "" : m[i] === "2" ? `◎${Number(u[i]).toLocaleString("ja-JP")}` : `${Number(u[i]).toLocaleString("ja-JP")}`}</td>
                                                    <td>{Number.isNaN(r[i]) || r[i] === "" ? "" : `${Number(r[i]).toLocaleString("ja-JP")}`}</td>
                                                    <td>{Number.isNaN(h[i]) || h[i] === "" ? "" : `${Number(h[i]).toLocaleString("ja-JP")}`}</td>
                                                </tr>
                                            ));
                                        })()}
                                    </tbody>
                                </table>
                            </dd>
                        </div>
                        <div className="d2">
                            <dt className="dt_template">売上伸長率</dt>
                            <dd className="dd_shincho">
                                {valueToText(kigyo.URIAGE_SHINCHORITSU) === "" ? "" : <><span>{valueToText(kigyo.URIAGE_SHINCHORITSU)}</span><span className="unit">%</span></>}
                            </dd>
                            <dt className="dt_template">利益伸長率</dt>
                            <dd className="dd_shincho">
                                {valueToText(kigyo.RIEKI_SHINCHORITSU) === "" ? "" : <><span>{valueToText(kigyo.RIEKI_SHINCHORITSU)}</span><span className="unit">%</span></>}
                            </dd>
                        </div>
                    </div>
                    <div style={{ width: "50%" }}>
                        <div className="d2">
                            <dt className="dt_template">取引銀行</dt>
                            <dd className="dd_torihiki">{kigyoInfoJoin(kigyo, "TORIHIKI_GINKO", 10)}</dd>
                        </div>
                        <div className="d2">
                            <dt className="dt_template">概況</dt>
                            <dd className="dd_torihiki">{valueToText(kigyo.GAIKYO)}</dd>
                        </div>
                    </div>
                </div>
                <div className="d1">
                    <dt className="dt_template">代表者</dt>
                    <dd className="dd_daihyo">{valueToText(kigyo.DAIHYOSHA)}</dd>
                    <dt className="dt_template">現住所</dt>
                    <dd className="dd_daihyo">
                        {valueToText(kigyo.GENJUSHO_YUBIN_NO) === "" ? "" : `〒${valueToText(kigyo.GENJUSHO_YUBIN_NO)}`}
                    </dd>
                    <dd className="dd_daihyoJ">{valueToText(kigyo.GENJUSHO_JUSHO)}</dd>
                </div>
                <div className="d1">
                    <dt className="dt_template">生年月日</dt>
                    <dd className="dd_birth">
                        {valueToText(kigyo.SEINENGAPPI) === "" ? "" : <><span>{valueToText(kigyo.SEINENGAPPI)}</span><span className="unit">生</span></>}
                    </dd>
                    <dt className="dt_template">干支</dt>
                    <dd className="dd_code">{valueToText(kigyo.ETO)}</dd>
                    <dt className="dt_template">出身地</dt>
                    <dd className="dd_code">{valueToText(kigyo.SHUSSHINCHI)}</dd>
                    <dt className="dt_template">出身校</dt>
                    <dd className="dd_code">{valueToText(kigyo.SHUSSHINKO)}</dd>
                </div>
            </dl>
            <div className="data_change">
                <div className="data_change_title">データ切り替え</div>
                <div className="error">{errorText}</div>
                <div className="pager_detail">
                    <button className="pager_detail_button" onClick={() => setIndex(0)} disabled={index <= 0}><FirstPage /></button>
                    <button className="pager_detail_button" onClick={() => setIndex(i => Math.max(i - 1, 0))} disabled={index <= 0}><ChevronLeft /></button>
                    <span><Box sx={{ fontWeight: "medium", fontSize: 18 }}>{index + 1}/{total}</Box></span>
                    <button className="pager_detail_button" onClick={() => setIndex(i => Math.min(i + 1, total - 1))} disabled={index >= total - 1}><ChevronRight /></button>
                    <button className="pager_detail_button" onClick={() => setIndex(total - 1)} disabled={index >= total - 1}><LastPage /></button>
                    <input
                        className={`pager-input ${errorText != "" ? "error" : ""}`}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={4}
                        value={pageText}
                        /*入力可能文字は半角数値のみ、4桁まで*/
                        onChange={(e) => {
                            const v = e.target.value;
                            if (/^\d{0,4}$/.test(v)) setPageText(v);
                        }} />
                    <GreenChoiceButton variant="contained" color="secondary" onClick={handleJumpPage}>番号指定</GreenChoiceButton>
                </div>
            </div>

            <div className="buttons">
                <GreenButton variant="contained" color="secondary" onClick={() => dispSearch()}>検索条件</GreenButton>
                <GreenButton variant="contained" color="secondary" onClick={() => dispKigyoInfoList()}>一覧表示</GreenButton>
                <GreenButton variant="contained" color="secondary" onClick={handleOpen}>PDF出力</GreenButton>
            </div>
            <PrintDialog isOpen={printDialogOpen} handleValue={printInfo} handleClose={() => setPrintDialogOpen(false)} />
        </>
    );
}
export default KigyoInfoDetail;
